import { observer } from 'src/utils/mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { DateTime } from 'src_common/utils/time/time';
import { SingleBetState } from 'src/components/BetSlip/SingleBet.state';
import { money } from 'src/utils/format';
import { autorun } from 'mobx';
import { FreeBetSelector } from 'src/components/BetSlip/BonusEngineFreeBets/FreeBetSelector';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { SingleBetPropsType } from 'src/components/BetSlip/SingleBet';
import { BetBuilderPriceStatusType } from 'src/components/BetSlip/BetBuilderPriceState';
import {
    BetBuilderLivePrice,
    OddsInput,
    BetNameWrapper,
    InputAndBetNameWrapper,
    BetBuilderBetSelection,
    BetBuilderBetNameInfo,
    BetBuilderBetSelections,
    BetBuilderBetMarket,
} from 'src/components/BetSlip/BetBuilderBet/BetBuilderBet.style';
import { fractionalToDecimal } from 'src/utils/helpers';
export type BetBuilderBetPropsType = SingleBetPropsType & {
    onSetBetBuilderPriceStatus: (status: BetBuilderPriceStatusType | null) => void;
};

export const BetBuilderBet = observer('BetBuilderBet', (propsIn: BetBuilderBetPropsType) => {
    const appState = useAppStateContext();
    const props = useAsObservableSource(propsIn);
    const common = useCommon();
    const { config, precision } = ConfigState.get(common);

    const [state] = useState(() => new SingleBetState(props, appState));
    const {
        selected,
        bet,
        leg,
        currencySymbol,
        isSelectedLeg,
        currency,
        betBonusEngineFreeBets,
        betBuilderPriceState,
    } = state;

    const bbPriceState = useMemo(() => betBuilderPriceState, []);

    useEffect(() => {
        const dispose = autorun(() => {
            const betStake = state.bet?.stakePerLine;
            const currentStake = state.stakePerLine;
            if (betStake !== undefined && betStake !== null && betStake != '0' && betStake !== currentStake) {
                state.setStakePerLine(betStake.toString());
            }
        });

        return (): void => {
            dispose();
            bbPriceState?.dispose();
        };
    }, []);

    useEffect(() => {
        state.onSetBetBuilderPriceStatusHandler(bbPriceState?.status ?? null);
    }, [bbPriceState?.status]);

    if (bet === null) {
        return null;
    }
    if (leg === null) {
        console.warn('LEG IS NULL OR UNDEFINED');
        return null;
    }

    const possibleReturnsEstimated = bet.potentialReturnsAt !== Math.round(state.betStake * 100);
    const getDecimalOdds = (price: string | number | null | undefined): number => {
        if (price === undefined || price === null) {
            return 0;
        }

        if (typeof price === 'string') {
            return fractionalToDecimal(price);
        }

        return price;
    };

    const normalizeStakePerLine = (stakePerLine: string | number | null | undefined): string => {
        if (stakePerLine == null) return '0';

        const normalizedStake = String(stakePerLine).trim();
        return normalizedStake === '' ? '0' : normalizedStake;
    };

    const computedPotentialReturns = precision
        .newFromAnything(normalizeStakePerLine(bet.stakePerLine))
        .multiply(getDecimalOdds(leg.price))
        .multiply(100).value;

    const possibleReturns = bet.potentialReturns ?? (computedPotentialReturns === '' ? null : computedPotentialReturns);

    let eventTimeFormatted = '';
    let competitionName = '';

    const eventModel = leg.eventId === undefined ? null : appState.sdkStaff.models.getEvent(leg.eventId);

    if (eventModel !== null) {
        eventTimeFormatted = DateTime.from(eventModel.timeSettingsStartTime)?.format('HH:mm DD/MM/YYYY') ?? '';
        competitionName = appState.sdkStaff.models.getCompetitionModel(eventModel.competition2.toOldId())?.name ?? '';
    }

    const priceChangedWarning = state.suspendedChange ? (
        <div className='betslip__price-change'>Selection is suspended.</div>
    ) : null;

    const clsName = classNames({
        'betslip-leg--marked': isSelectedLeg,
    });

    const bbSelections = useMemo(
        () => (leg.name ?? '').split(', ').map((selection) => selection.split(' | ')),
        [leg.name]
    );

    return (
        <li className={clsName}>
            <div className='betslip-leg-amount single'>
                <div className='row'>
                    <div className='col-5'>
                        <div className={classNames(['element', { 'bet-red-border': state.suspendedChange }])}>
                            <OddsInput
                                type='text'
                                readOnly
                                value={state.getPrice}
                                onChange={state.onUpdatePriceHandler}
                                onBlur={state.onSavePriceHandler}
                                data-test='single-stake-price'
                                suspendedChange={state.suspendedChange}
                            />
                        </div>
                    </div>
                    <div className='col-7'>
                        <div className='element'>
                            <span className='prefix'>{currencySymbol}</span>
                            <input
                                type='text'
                                value={state.getStakePerLine}
                                onChange={state.onChangeStakePerLine}
                                onBlur={state.saveStake}
                                onFocus={state.cleanStake}
                                data-test='single-stake-input'
                            />
                        </div>
                    </div>
                </div>
                {betBonusEngineFreeBets.some((fb) => fb.freeBets.length > 0) && config.showBonusEngine === true && (
                    <FreeBetSelector
                        betUid={bet.uid}
                        betType={bet.type}
                        userId={props.userId}
                        bonusEngineFreeBets={betBonusEngineFreeBets}
                        currency={typeof currency === 'string' ? currency : ''}
                        currencySymbol={currencySymbol}
                        selectedFreeBets={props.selectedFreeBets}
                        toggleFreeBet={props.toggleFreeBet}
                        clearFreeBetSelection={props.clearFreeBetSelection}
                    />
                )}
            </div>

            <BetNameWrapper>
                <InputAndBetNameWrapper>
                    <input
                        type='checkbox'
                        onChange={state.toggle}
                        checked={selected}
                    />
                    <BetBuilderBetSelections>
                        {bbSelections.map(([name, market]) => (
                            <li key={`${name}-${market}`}>
                                <BetBuilderBetSelection>
                                    <span>{name}</span>
                                    <BetBuilderBetMarket>{market}</BetBuilderBetMarket>
                                </BetBuilderBetSelection>
                            </li>
                        ))}
                    </BetBuilderBetSelections>
                </InputAndBetNameWrapper>

                <BetBuilderBetNameInfo>
                    {`Bet Builder (${leg.provider ?? 'Genius'})`}
                    <br />
                    {eventModel?.name ?? ''}
                    <br />
                    {competitionName}
                    <br />
                    {eventTimeFormatted}
                </BetBuilderBetNameInfo>

                {typeof bbPriceState?.livePriceInfo === 'string' ? (
                    <BetBuilderLivePrice>{bbPriceState.livePriceInfo}</BetBuilderLivePrice>
                ) : null}
            </BetNameWrapper>

            <div
                className={classNames([
                    'betslip-leg-returns',
                    { 'betslip-leg-returns--estimated': possibleReturnsEstimated },
                ])}
            >
                {possibleReturns === null ? null : <div>Possible returns: {money(possibleReturns, currency)}</div>}
                {bet.maxStake !== null && bet.maxStake !== undefined ? (
                    <div className='betslip-leg-maxstake'>Cust. max bet: {money(bet.maxStake, currency)}</div>
                ) : null}
                {(bet.isSplitted === undefined || bet.isSplitted === false) &&
                bet.isReferred === true &&
                props.isOffered === false ? (
                    <button
                        className='btn btn--split-bet'
                        onClick={state.onAddSplitBetHandler}
                        disabled
                    >
                        Split bet
                    </button>
                ) : null}
            </div>

            {priceChangedWarning}
        </li>
    );
});
